/**
 * Payment Mode Selector Web Component
 *
 * Provides three mutually exclusive checkbox-style payment modes on the PDP:
 * - "default"  → standard add-to-cart, HeyLight widget hidden
 * - "widget"   → shows HeyLight Financing price-per-month widget
 * - "redirect" → shows leasing price, button redirects instead of adding to cart
 *
 * ─── WHY click interception instead of submit interception ───
 *
 * Horizon's Component class registers event handlers on `document` with { capture: true }
 * (see component.js registerEventListeners). For the submit event, this means Horizon's
 * ProductFormComponent.handleSubmit fires BEFORE any handler on .shopify-section because
 * capture phase flows from document → ... → section → form. By the time a section-level
 * handler runs, handleSubmit has already called fetch('/cart/add.js').
 *
 * Solution: intercept the add-to-cart button CLICK instead. The button has type="submit",
 * so clicking it fires click → then submit (as the default action of the click).
 * If we call event.preventDefault() on the click, the submit event never fires.
 * Horizon's click handler (handleClick) only does animation — it doesn't add to cart.
 * So our click handler fires after Horizon's animation, cancels the default action,
 * and the form never submits. We redirect instead.
 *
 * ─── Other integration points ───
 *
 * - Listens for `variant:update` to sync variant ID and price.
 * - Controls visibility of the HeyLight widget container [data-heylight-widget].
 * - Calculates and displays leasing monthly price in [data-leasing-price].
 * - Dispatches `payment:mode-change` CustomEvent on window for other components.
 * - Supports a "rent" mode backed by product metafields (rent_6, rent_12, rent_24).
 *   Prices are read from data attributes; term-slider toggles between them.
 */
(() => {
  if (customElements.get('payment-mode-selector')) return;

  class PaymentModeSelector extends HTMLElement {
    constructor() {
      super();
      this.onModeChange = this.onModeChange.bind(this);
      this.onVariantUpdate = this.onVariantUpdate.bind(this);
      this.onAddToCartClick = this.onAddToCartClick.bind(this);
      this.onTermDotClick = this.onTermDotClick.bind(this);
    }

    connectedCallback() {
      this.currentMode = 'default';
      this.variantId = this.dataset.variantId || '';
      this.variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this.productHandle = this.dataset.productHandle || '';
      this.redirectBaseUrl = this.dataset.redirectBaseUrl || '';
      this.leasingMonths = parseInt(this.dataset.leasingMonths, 10) || 36;
      this.leasingPriceTemplate = this.dataset.leasingPriceTemplate || '[amount]';

      this.heylightSdkUrl = this.dataset.heylightSdkUrl || '';
      this.heylightSdkLoaded = false;

      // Rent data (prices from product metafields, stored in major currency units, e.g. 150 = CHF 150/month).
      // NOTE: we use getAttribute instead of dataset because the HTML spec does NOT strip hyphens
      // before digits during camelCase conversion, so dataset.rentPrice6 would be undefined for
      // the attribute data-rent-price-6 (the hyphen before '6' stays, key becomes 'rentPrice-6').
      this.showRent = this.dataset.showRent === 'true';
      this.rentPrices = {
        6: parseFloat(this.getAttribute('data-rent-price-6')) || 0,
        12: parseFloat(this.getAttribute('data-rent-price-12')) || 0,
        24: parseFloat(this.getAttribute('data-rent-price-24')) || 0,
      };
      this.rentRedirectBaseUrl = this.dataset.rentRedirectBaseUrl || '';
      this.rentPriceTemplate = this.dataset.rentPriceTemplate || '[amount]';
      this.rentMonths = 0; // will be set from slider initial position

      this.sectionContainer = this.closest('.shopify-section');

      // Internal content panels
      this.priceDefault = this.querySelector('[data-price-default]');
      this.heylightWidget = this.querySelector('[data-heylight-widget]');
      this.leasingInfo = this.querySelector('[data-leasing-info]');
      this.leasingPriceEl = this.querySelector('[data-leasing-price]');
      this.rentInfo = this.querySelector('[data-rent-info]');
      this.rentPriceEl = this.querySelector('[data-rent-price]');

      // Cache all price containers inside this component.
      // We removed data-product-id from our <product-price> elements so the
      // native product-price.js handler ignores them — no more race conditions
      // with multiple <product-price> nodes competing for response DOM nodes.
      this.priceContainers = Array.from(
        this.querySelectorAll('product-price[data-price-default] [ref="priceContainer"]')
      );
      if (this.currentMode === 'widget') {
        this.updateWidgetPrice();
      }

      // Term sliders — each [data-term-slider] is scoped by mode name.
      // Stores a Map<string, { container, steps, currentIndex, labels, dots, trackFill }> keyed by mode.
      this.termSliders = new Map();
      this.querySelectorAll('[data-term-slider]').forEach((container) => {
        const mode = container.dataset.termSlider;
        const steps = (container.dataset.termSteps || '').split(',').map(Number).filter(Boolean);
        const labels = container.querySelectorAll('[data-term-label]');
        const dots = container.querySelectorAll('[data-term-dot]');
        const trackFill = container.querySelector('[data-term-track-fill]');
        // Read initial active dot index (set server-side via Liquid)
        let currentIndex = 0;
        dots.forEach((dot) => {
          if (dot.classList.contains('is-active')) {
            currentIndex = parseInt(dot.dataset.dotIndex, 10) || 0;
          }
        });
        this.termSliders.set(mode, { container, steps, currentIndex, labels, dots, trackFill });
      });

      // Set initial active labels for all sliders
      this.termSliders.forEach((slider) => this.syncSliderLabels(slider));

      // Override leasing months from slider's initial position if present
      const redirectSlider = this.termSliders.get('redirect');
      if (redirectSlider) {
        const term = redirectSlider.steps[redirectSlider.currentIndex];
        if (term) this.leasingMonths = term;
      }

      // Override rent months from slider's initial position if present
      const rentSlider = this.termSliders.get('rent');
      if (rentSlider) {
        const rentTerm = rentSlider.steps[rentSlider.currentIndex];
        if (rentTerm) this.rentMonths = rentTerm;
      }

      // Sync HeyLight widget term with the widget slider's initial position.
      // The Liquid renders data-heidipay-term from the last acceptable term,
      // but the slider defaults to index 0 (first/shortest term) — align them.
      const widgetSlider = this.termSliders.get('widget');
      if (widgetSlider) {
        const initialTerm = widgetSlider.steps[widgetSlider.currentIndex];
        if (initialTerm) {
          const heylightContainer = this.querySelector('[data-heidipay="true"]');
          if (heylightContainer) {
            heylightContainer.setAttribute('data-heidipay-term', String(initialTerm));
          }
        }
      }

      // Hide the widget wrapper on init — SDK is loaded lazily on first widget selection,
      // ensuring it initialises into a visible container (no display:none race condition).
      if (this.heylightWidget) {
        this.heylightWidget.setAttribute('hidden', '');
      }

      // Find the add-to-cart button in the same section.
      // The button lives inside <product-form-component> → <form> → <add-to-cart-component> → <button name="add">
      this.addToCartButton = this.sectionContainer?.querySelector(
        'product-form-component button[name="add"]'
      );

      this.setEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setEventListeners() {
      // Radio button changes within this component
      this.addEventListener('change', this.onModeChange);

      // Variant sync — variant:update bubbles from variant-picker through the section
      if (this.sectionContainer) {
        this.sectionContainer.addEventListener('variant:update', this.onVariantUpdate);
      }

      // Click interception on the add-to-cart button for redirect mode.
      // This fires at the target/bubble phase AFTER Horizon's document-capture handler
      // (which only does animation via handleClick). We call preventDefault() to cancel
      // the button's default action (form submission), then redirect.
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', this.onAddToCartClick);
      }

      // Term sliders — bind per instance
      this.termSliders.forEach((slider) => {
        slider.dots.forEach((dot) => {
          dot.addEventListener('click', this.onTermDotClick);
        });
      });
    }

    removeEventListeners() {
      this.removeEventListener('change', this.onModeChange);

      if (this.sectionContainer) {
        this.sectionContainer.removeEventListener('variant:update', this.onVariantUpdate);
      }

      if (this.addToCartButton) {
        this.addToCartButton.removeEventListener('click', this.onAddToCartClick);
      }

      this.termSliders.forEach((slider) => {
        slider.dots.forEach((dot) => {
          dot.removeEventListener('click', this.onTermDotClick);
        });
      });
    }

    /**
     * Handles radio button change events.
     * Updates checkbox visual state, toggles widget/leasing visibility, dispatches event.
     *
     * @param {Event} event
     */
    onModeChange(event) {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!event.target.matches('[data-payment-mode]')) return;

      this.currentMode = event.target.value;

      // Update visual checkbox states
      this.querySelectorAll('[data-checkbox]').forEach((checkbox) => {
        checkbox.classList.remove('payment-mode-selector__checkbox--checked');
      });
      const activeOption = event.target.closest('.payment-mode-selector__option');
      const activeCheckbox = activeOption?.querySelector('[data-checkbox]');
      if (activeCheckbox) {
        activeCheckbox.classList.add('payment-mode-selector__checkbox--checked');
      }

      // Toggle default price
      if (this.priceDefault) {
        if (this.currentMode === 'default') {
          this.priceDefault.removeAttribute('hidden');
        } else {
          this.priceDefault.setAttribute('hidden', '');
        }
      }

      // Toggle HeyLight widget wrapper
      if (this.heylightWidget) {
        if (this.currentMode === 'widget') {
          this.heylightWidget.removeAttribute('hidden');
          // Load SDK lazily on first selection so it initialises into a visible container.
          this.loadHeylightSdk();
          this.updateWidgetPrice();
        } else {
          this.heylightWidget.setAttribute('hidden', '');
        }
      }

      // Toggle leasing info
      if (this.leasingInfo) {
        if (this.currentMode === 'redirect') {
          this.leasingInfo.removeAttribute('hidden');
          this.updateLeasingPrice();
        } else {
          this.leasingInfo.setAttribute('hidden', '');
        }
      }

      // Toggle rent info
      if (this.rentInfo) {
        if (this.currentMode === 'rent') {
          this.rentInfo.removeAttribute('hidden');
          this.updateRentPrice();
        } else {
          this.rentInfo.setAttribute('hidden', '');
        }
      }

      // Notify other components
      this.dispatchModeChange();
    }

    /**
     * Dispatches payment:mode-change on window with all redirect/rent data.
     * Called on mode change and whenever a term slider moves so that
     * the sticky-add-to-cart always has the latest months value.
     */
    dispatchModeChange() {
      window.dispatchEvent(
        new CustomEvent('payment:mode-change', {
          detail: {
            mode: this.currentMode,
            handle: this.productHandle,
            variantId: this.variantId,
            redirectBaseUrl: this.redirectBaseUrl,
            leasingMonths: this.leasingMonths,
            rentRedirectBaseUrl: this.rentRedirectBaseUrl,
            rentMonths: this.rentMonths,
          },
        })
      );
    }

    /* ── Term slider handlers ── */

    /**
     * Resolves the slider instance from a DOM event target.
     *
     * @param {Event} event
     * @returns {{ mode: string, slider: Object } | null}
     */
    resolveSlider(event) {
      const container = /** @type {HTMLElement} */ (event.target).closest('[data-term-slider]');
      if (!container) return null;
      const mode = container.dataset.termSlider;
      const slider = this.termSliders.get(mode);
      return slider ? { mode, slider } : null;
    }

    /**
     * Handles clicks on dot indicators to snap the slider to that step.
     *
     * @param {Event} event
     */
    onTermDotClick(event) {
      const dot = /** @type {HTMLElement} */ (event.target).closest('[data-term-dot]');
      if (!dot) return;
      const resolved = this.resolveSlider(event);
      if (!resolved) return;
      const index = parseInt(dot.dataset.dotIndex, 10);
      if (isNaN(index) || index < 0) return;
      this.applyTerm(resolved.mode, resolved.slider, index);
    }

    /**
     * Applies the selected term for a specific slider:
     * - "widget" → updates HeyLight SDK attribute and re-inits
     * - "redirect" → updates leasing months and recalculates price
     *
     * @param {string} mode - Slider mode identifier
     * @param {Object} slider - Slider descriptor from this.termSliders
     * @param {number} index - Index into slider.steps
     */
    applyTerm(mode, slider, index) {
      const term = slider.steps[index];
      if (!term) return;

      slider.currentIndex = index;

      if (mode === 'widget') {
        const heylightContainer = this.querySelector('[data-heidipay="true"]');
        if (heylightContainer) {
          heylightContainer.setAttribute('data-heidipay-term', String(term));
          clearTimeout(this._heylightReinitTimer);
          this._heylightReinitTimer = setTimeout(() => {
            window.initCoreHeidiCode?.();
          }, 100);
        }
      }

      if (mode === 'redirect') {
        this.leasingMonths = term;
        this.updateLeasingPrice();
        this.dispatchModeChange();
      }

      if (mode === 'rent') {
        this.rentMonths = term;
        this.updateRentPrice();
        this.dispatchModeChange();
      }

      this.syncSliderLabels(slider);
    }

    /**
     * Highlights the label and dot that match the current slider position,
     * and updates the track fill width.
     *
     * @param {Object} slider - Slider descriptor from this.termSliders
     */
    syncSliderLabels(slider) {
      const currentIndex = slider.currentIndex;
      const currentTerm = slider.steps[currentIndex];

      // Sync label active states
      slider.labels.forEach((label) => {
        const labelTerm = parseInt(label.dataset.termLabel, 10);
        label.classList.toggle('is-active', labelTerm === currentTerm);
      });

      // Sync dot active / passed states
      slider.dots.forEach((dot) => {
        const dotIndex = parseInt(dot.dataset.dotIndex, 10);
        dot.classList.toggle('is-active', dotIndex === currentIndex);
        dot.classList.toggle('is-passed', dotIndex < currentIndex);
      });

      // Update track fill width
      this.updateTrackFill(slider, currentIndex);
    }

    /**
     * Sets the red fill width on the track proportional to the current step.
     *
     * @param {Object} slider - Slider descriptor from this.termSliders
     * @param {number} index - Current step index
     */
    updateTrackFill(slider, index) {
      if (!slider.trackFill) return;
      const maxIndex = slider.steps.length - 1;
      const pct = maxIndex > 0 ? (index / maxIndex) * 100 : 0;
      slider.trackFill.style.width = pct + '%';
    }

    /**
     * Lazily injects the HeyLight SDK script the first time the widget mode is selected.
     * By the time this runs, [data-heylight-widget] is already visible, so the SDK
     * initialises into a properly laid-out container (no display:none race condition).
     */
    loadHeylightSdk() {
      if (!this.heylightSdkUrl || this.heylightSdkLoaded) return;
      this.heylightSdkLoaded = true;

      const script = document.createElement('script');
      script.src = this.heylightSdkUrl;
      document.head.appendChild(script);
    }

    /**
     * Formats a price in minor units using Shopify's formatter when available,
     * Always formats as "CHF 8,948.00" (comma thousands separator, dot decimal).
     *
     * @param {number} cents
     * @returns {string}
     */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const [integer, decimal] = amount.split('.');
      const thousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return "CHF " + thousands + "." + decimal;
    }

    updateWidgetPrice() {
      if (!this.variantPrice) return;
      const financingPrice = this.variantPrice;
      const formatted = this.formatMoney(financingPrice);
      
      // Met à jour data-heidipay-minorAmount avec le prix +5%
      const heylightContainer = this.querySelector('[data-heidipay="true"]');
      if (heylightContainer) {
        heylightContainer.setAttribute('data-heidipay-minorAmount', String(financingPrice));
      }
      
      // Met à jour l'affichage du prix dans le bloc HeyLight
      const widgetPriceContainer = this.heylightWidget?.querySelector('[ref="priceContainer"]');
      if (widgetPriceContainer) {
        // Cherche tous les éléments de prix possibles dans le container Horizon
        const priceEl =
        widgetPriceContainer.querySelector('[data-price]') ||
        widgetPriceContainer.querySelector('.price__current') ||
        widgetPriceContainer.querySelector('.price');
        if (priceEl) {
          priceEl.textContent = formatted;
        }
      }
    }

    /**
     * Reads the rent price for the currently selected term from data attributes
     * and displays it formatted.
     *
     * Metafield prices are stored in major currency units (e.g. 150 = CHF 150 / month)
     * so we multiply by 100 before passing to formatMoney() which divides back by 100.
     */
    updateRentPrice() {
      if (!this.rentPriceEl) return;

      // Fallback: if rentMonths is not yet set, use the first available term
      if (!this.rentMonths) {
        const firstTerm = Object.keys(this.rentPrices).find((k) => this.rentPrices[k] > 0);
        if (firstTerm) this.rentMonths = Number(firstTerm);
      }

      const price = this.rentPrices[this.rentMonths];
      if (!price) {
        this.rentPriceEl.textContent = '';
        return;
      }

      // Metafield value is in major units → convert to minor units for formatMoney
      const formatted = this.formatMoney(Math.round(price * 100));
      this.rentPriceEl.textContent = this.rentPriceTemplate.replace('[amount]', formatted);
    }

    // ── Leasing constants ────────────────────────────────────────────────────
    // TVA Suisse 8.1 %
    static VAT_RATE = 1.081;
    // Valeur résiduelle fixe TTC (CHF), identique pour toutes les durées
    static RESIDUAL_TTC = 540;
    // Taux d'intérêt leasing annuel
    static LEASING_ANNUAL_RATE = 0.0;

    // ── Tables assurance mensuelle (Suisse Alpine Service AG) ────────────────
    // Clé = prix TTC max de la tranche ; valeur = prime mensuelle CHF
    // Courte durée  : 6 – 24 mois (sans extension de garantie)
    static INSURANCE_SHORT = [
      { maxPrice:  2000, monthly: 10.40 },
      { maxPrice:  4000, monthly: 15.40 },
      { maxPrice:  6000, monthly: 20.80 },
      { maxPrice:  8000, monthly: 25.60 },
      { maxPrice: 10000, monthly: 31.60 },
      { maxPrice: 12000, monthly: 39.30 },
      { maxPrice: 14000, monthly: 45.90 },
      { maxPrice: 16000, monthly: 52.60 },
      { maxPrice: 18000, monthly: 64.80 },
      { maxPrice: 20000, monthly: 73.20 },
    ];
    // Longue durée : 25 – 48 mois (avec extension de garantie)
    static INSURANCE_LONG = [
      { maxPrice:  2000, monthly: 15.60 },
      { maxPrice:  4000, monthly: 20.60 },
      { maxPrice:  6000, monthly: 25.60 },
      { maxPrice:  8000, monthly: 30.80 },
      { maxPrice: 10000, monthly: 36.00 },
      { maxPrice: 12000, monthly: 43.00 },
      { maxPrice: 14000, monthly: 54.80 },
      { maxPrice: 16000, monthly: 61.80 },
      { maxPrice: 18000, monthly: 69.90 },
      { maxPrice: 20000, monthly: 76.80 },
    ];

    /**
     * Returns the monthly insurance premium for a given price and term.
     * Uses the short table for 6–24 months, long table for 25–48 months.
     * Falls back to the last row if the price exceeds all defined tranches.
     *
     * @param {number} priceTTC  - Variant price TTC in major currency units (e.g. 7699)
     * @param {number} months    - Leasing term in months
     * @returns {number}         - Monthly insurance premium in CHF
     */
    static getInsurance(priceTTC, months) {
      const table = months <= 24
        ? PaymentModeSelector.INSURANCE_SHORT
        : PaymentModeSelector.INSURANCE_LONG;
      const row = table.find((r) => priceTTC <= r.maxPrice);
      return row ? row.monthly : table[table.length - 1].monthly;
    }

    /**
     * Calculates and displays the leasing monthly price.
     *
     * Formula (verified against partner Excel sheet):
     *
     *   priceHT    = priceTTC / 1.081
     *   residualHT = 540 / 1.081
     *   intérêts   = (priceHT + residualHT) / 2 × (4.6% / 12)
     *   amort.     = (priceHT − residualHT) / nb_mois
     *   leasingTTC = (intérêts + amort.) × 1.081
     *   assurance  = table selon prix TTC et durée
     *   mensualité = leasingTTC + assurance
     *
     * Vérification Maverick Beast (7 699 CHF TTC, 48 mois) :
     *   priceHT    = 7 122.11   residualHT = 499.54
     *   intérêts   = 14.52      amort.     = 138.38
     *   leasingTTC = 165.28     assurance  = 30.80
     *   → mensualité ≈ 196.08 CHF  (tableau partenaire : 195.74 CHF ✓)
     */
    updateLeasingPrice() {
      if (!this.leasingPriceEl || !this.variantPrice) return;

      // variantPrice is in minor units (cents) → convert to major units (CHF)
      const priceTTC = this.variantPrice / 100;
      const months   = this.leasingMonths;

      const VAT      = PaymentModeSelector.VAT_RATE;
      const RESIDUAL = PaymentModeSelector.RESIDUAL_TTC;
      const RATE     = PaymentModeSelector.LEASING_ANNUAL_RATE;

      const priceHT    = priceTTC / VAT;
      const residualHT = RESIDUAL / VAT;

      const interest     = ((priceHT + residualHT) / 2) * (RATE / 12);
      const amortisation = (priceHT - residualHT) / months;
      const leasingTTC   = (interest + amortisation) * VAT;

      const insurance    = PaymentModeSelector.getInsurance(priceTTC, months);

      const monthly = leasingTTC + insurance;

      // formatMoney expects minor units (cents)
      const formatted = this.formatMoney(Math.round(monthly * 100));
      this.leasingPriceEl.textContent = this.leasingPriceTemplate.replace('[amount]', formatted);
    }

    /**
     * Syncs variant ID and price when the user switches variants.
     *
     * @param {Event} event
     */

    onVariantUpdate(event) {
      const detail = /** @type {any} */ (event).detail;
      const resource = detail?.resource;

      if (resource?.id) {
        this.variantId = String(resource.id);
      }

      if (resource?.price != null) {
        this.variantPrice = resource.price;

        // Update all price containers inside this component from the
        // response HTML. We query inside the response's own
        // <payment-mode-selector> to get the server-rendered price with
        // correct locale / currency formatting — no race with native
        // product-price.js.
        const responsePms = detail.data?.html?.querySelector('payment-mode-selector');
        if (responsePms) {
          const newPriceEl = responsePms.querySelector(
            'product-price[data-price-default] [ref="priceContainer"]'
          );
          if (newPriceEl) {
            const newHTML = newPriceEl.innerHTML;
            this.priceContainers.forEach((container) => {
              if (container.innerHTML !== newHTML) {
                container.innerHTML = newHTML;
              }
            });
          }
        }

        const heylightContainer = this.querySelector('[data-heidipay="true"]');
        if (heylightContainer) {
          heylightContainer.setAttribute('data-heidipay-minorAmount', String(resource.price));
          clearTimeout(this._heylightReinitTimer);
          this._heylightReinitTimer = setTimeout(() => {
            window.initCoreHeidiCode?.();
          }, 100);
        }

        if (this.currentMode === 'widget') {
          this.updateWidgetPrice();
        }

        if (this.currentMode === 'redirect') {
          this.updateLeasingPrice();
        }

        if (this.currentMode === 'rent') {
          this.updateRentPrice();
        }
      }

      // Re-acquire add-to-cart button in case morph replaced the DOM
      const newButton = this.sectionContainer?.querySelector(
        'product-form-component button[name="add"]'
      );
      if (newButton && newButton !== this.addToCartButton) {
        if (this.addToCartButton) {
          this.addToCartButton.removeEventListener('click', this.onAddToCartClick);
        }
        this.addToCartButton = newButton;
        this.addToCartButton.addEventListener('click', this.onAddToCartClick);
      }

      // Ensure all variant option groups have a valid selection after morph.
      // When one option changes (e.g. Frame Color), the variant picker morphs
      // and may remove previously-checked values that are no longer available
      // (e.g. a Rim Color). If a fieldset is left without a :checked radio,
      // auto-select the first available value and re-trigger the native change
      // flow so the picker resolves to a valid variant + correct price.
      this.ensureVariantSelections();
    }

    /**
     * Intercepts add-to-cart button click when mode is "redirect".
     *
     * How it works:
     * 1. User clicks the add-to-cart button (type="submit").
     * 2. Horizon's document-level capture handler fires FIRST — calls handleClick (animation only):
     *    adds 'atc-added' class to the button and appends <fly-to-cart> to <body>.
     * 3. Our handler fires on the button (bubble phase) — calls preventDefault() which cancels
     *    the button's default action (form submission). The submit event never fires.
     * 4. We immediately undo the animation Horizon started (remove class + fly-to-cart element).
     * 5. We redirect to the leasing page.
     *
     * @param {MouseEvent} event
     */
    onAddToCartClick(event) {
      if (this.currentMode !== 'redirect' && this.currentMode !== 'rent') return;

      // Cancel the button's default action (triggering form submit)
      event.preventDefault();

      // Horizon's document-capture handler already fired handleClick() which added
      // 'atc-added' to the button and appended a <fly-to-cart> to <body> — both
      // need to be undone because nothing is actually being added to the cart.
      this.addToCartButton?.classList.remove('atc-added');
      document.querySelectorAll('fly-to-cart').forEach((el) => el.remove());

      if (this.currentMode === 'rent') {
        const rentBase = this.rentRedirectBaseUrl || window.location.pathname;
        const rentSep = rentBase.includes('?') ? '&' : '?';
        const rentParams = [
          'product_handle=' + encodeURIComponent(this.productHandle),
          'selected_variant_id=' + encodeURIComponent(this.variantId),
          'rent_months=' + encodeURIComponent(this.rentMonths),
        ].join('&');

        window.location.href = rentBase + rentSep + rentParams;
        return;
      }

      const baseUrl = this.redirectBaseUrl || window.location.pathname;
      const separator = baseUrl.includes('?') ? '&' : '?';
      const params = [
        'product_handle=' + encodeURIComponent(this.productHandle),
        'selected_variant_id=' + encodeURIComponent(this.variantId),
        'leasing_months=' + encodeURIComponent(this.leasingMonths),
      ].join('&');

      window.location.href = baseUrl + separator + params;
    }

    /**
     * Ensures every variant option group in the variant picker has a valid
     * selection after the morph cycle.
     *
     * When one option changes (e.g. Frame Color), the morph may remove
     * previously-checked radio buttons for a dependent option (e.g. Rim Color)
     * because the Liquid template skips unavailable values. This leaves a
     * fieldset with no :checked input, cascading into an invalid variant state.
     *
     * This method detects that situation and programmatically selects the first
     * available value, dispatching a native change event so the variant picker
     * processes a second fetch → morph → variant:update cycle that converges
     * on a valid variant with correct pricing.
     *
     * Runs at most one auto-select per cycle to avoid firing multiple change
     * events simultaneously. Second cycle resolves cleanly (no infinite loop).
     */
    ensureVariantSelections() {
      const variantPicker = this.sectionContainer?.querySelector('variant-picker');
      if (!variantPicker) return;

      // Check radio-based fieldsets (swatch / button styles)
      const fieldsets = variantPicker.querySelectorAll('fieldset[data-fieldset-index]');
      for (const fieldset of fieldsets) {
        if (fieldset.querySelector('input:checked')) continue;

        const firstAvailable = /** @type {HTMLInputElement | null} */ (
          fieldset.querySelector('input[data-option-available="true"]') ||
          fieldset.querySelector('input[type="radio"]')
        );

        if (firstAvailable) {
          firstAvailable.checked = true;
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
          return; // one auto-select per cycle
        }
      }

      // Check dropdown selects
      const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
        variantPicker.querySelectorAll('select.variant-option__select')
      );
      for (const select of selects) {
        if (select.querySelector('option[selected]')) continue;

        const browserSelected = select.options[select.selectedIndex];
        if (browserSelected) {
          browserSelected.setAttribute('selected', 'selected');
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  customElements.define('payment-mode-selector', PaymentModeSelector);
})();